import {AxiosInstance, AxiosResponse} from "axios";
import {PaginatedData} from "@/core/interfaces/api";
import {FlowchartInstance, FlowchartInstancePath} from "@/core/interfaces/flowchart/flowchart";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";

/**
 * The Flowchart resource.
 * @param {Object} client - The injected client.
 */
export default function (client: AxiosInstance) {
    return {
        /**
         * Get flowchart instance index.
         */
        async index(params: object): Promise<PaginatedData<FlowchartInstance[]>> {
            return await client.get('flowchart-instances', {params})
                .then((response: AxiosResponse) => response.data);
        },
        /**
         * Show a flowchart instance.
         *
         * @param flowchartId  The flowchart ID.
         */
        async show(flowchartId: number): Promise<FlowchartInstance> {
            return await client.get(`flowcharts/${flowchartId}`)
                .then((response: AxiosResponse) => response.data.data)
                .catch(() => []);
        },
        /**
         * Store a flowchart instance.
         *
         * @param flowchartId  The flowchart ID.
         * @param consumer  The patient the flowchart is run for.
         * @param path  The path.
         */
        async store(flowchartId: number, consumer: Customer | Beneficiary, path: FlowchartInstancePath): Promise<FlowchartInstance> {
            return await client.post(`flowchart-instances`, {
                flowchart_id: flowchartId,
                consumer_key: consumer.consumer_key,
                path
            })
                .then((response: AxiosResponse) => response.data.data);
        },
        /**
         * Update the path of a flowchart instance.
         *
         * @param instanceId  The flowchart instance ID.
         * @param path  The path.
         */
        async update(instanceId: number, path: FlowchartInstancePath): Promise<FlowchartInstance> {
            return await client.patch(`flowchart-instances/${instanceId}`, {path})
                .then((response: AxiosResponse) => response.data.data);
        },
        /**
         * Delete a flowchart instance.
         *
         * @param instanceId  The flowchart instance ID.
         */
        async destroy(instanceId: number): Promise<void> {
            await client.delete(`flowchart-instances/${instanceId}`);
        }
    };
}
